<template>
	<AppCard
		v-if="loan"
		v-model:is-collapsed="isCollapsed"
		no-padding
		collapsible
		top-section-class="p-2"
		class="text-sm"
	>
		<template #title>
			<div class="flex items-center gap-2">
				<img
					v-if="loan.lenderCode && getLenderOption(loan.lenderCode)"
					:src="`/logos/${getLenderOption(loan.lenderCode)?.icon}`"
					class="w-4 h-4"
				/>
				{{ titleForMortgage(loan, getLenderOption(loan.lenderCode)?.label) }}
			</div>
		</template>
		<template #subtitle>
			<AppButton
				variant="ghost"
				icon="delete"
				aria-label="Verwijder hypotheek"
				@click="removeMortgage"
			/>
		</template>
		<div class="px-5 py-2 flex flex-col gap-2">
			<div class="flex gap-2">
				<AppAutocomplete
					v-model="loan.lenderCode"
					label="Geldverstrekker"
					empty-message="Geen geldverstrekkers gevonden"
					:has-icon="true"
					:class="{
						'readonly-bordered-input': mortgageReferenceFieldsReadonly,
					}"
					:options="lenderOptions"
					:option-loader="getLenderOption"
					:open-on-focus="true"
					:disabled="mortgageReferenceFieldsReadonly"
				/>
				<AppInput
					v-model="loan.lenderMortgageId"
					label="Hypotheeknummer"
					:class="{
						'readonly-bordered-input': mortgageReferenceFieldsReadonly,
					}"
					:readonly="mortgageReferenceFieldsReadonly"
				/>
			</div>

			<AppInput
				v-if="showOwnerField"
				v-model="loan.owner"
				label="Eigenaar"
				type="option"
				:options="ownerOptions"
				:class="[
					'max-w-50',
					{ 'readonly-bordered-input': mortgageReferenceFieldsReadonly },
				]"
				:readonly="mortgageReferenceFieldsReadonly"
			/>

			<AppInput
				v-if="showCurrentObjectField"
				v-model="loan.currentObjectId"
				label="Huidig object"
				type="option"
				:options="currentObjectOptions"
				class="max-w-80"
			/>

			<div
				v-if="showInformatieBerichtUi"
				class="flex flex-col items-start gap-2"
			>
				<ul
					v-if="
						Array.isArray(requestMortgageDataValidation) && !informatieAanvraag
					"
					class="list-disc list-inside text-red-500"
				>
					<li v-for="error in requestMortgageDataValidation" :key="error">
						{{ error }}
					</li>
				</ul>

				<template v-if="informatieAanvraag">
					<div class="flex items-center gap-2">
						<AppIcon
							:name="statusIcon"
							class="text-lg"
							:class="statusIconColorClass"
						/>
						<div class="flex flex-col">
							<span class="font-medium">{{ statusDisplayName }}</span>
							<span v-if="statusDescription" class="text-xs text-gray-600">
								{{ statusDescription }}
							</span>
						</div>
					</div>
				</template>

				<AppButton
					v-else
					variant="primary"
					:disabled="requestMortgageDataValidation !== true"
					@click="requestMortgageData"
				>
					Actuele stand opvragen
				</AppButton>

				<AppButton
					v-if="informatieAanvraag?.response"
					variant="secondary"
					@click="applyMortgageData"
				>
					Gegevens meenemen
				</AppButton>
			</div>

			<AppButton
				v-if="showManualEditingButton"
				variant="link"
				class="self-start"
				@click="toggleManualEditing"
			>
				{{ manualEditingButtonLabel }}
			</AppButton>

			<template v-if="showDetails">
				<div class="flex gap-2">
					<AppInput
						v-model="loan.amountRegistration"
						label="Inschrijving"
						:value-type="ValueType.Currency"
						:class="[
							'max-w-50',
							{ 'readonly-bordered-input': detailsReadonly },
						]"
						:readonly="detailsReadonly"
					/>

					<AppInput
						v-model="loan.nhg"
						label="NHG"
						type="option"
						:options="[
							{ label: 'Ja', value: true },
							{ label: 'Nee', value: false },
						]"
						:class="[
							'max-w-50',
							{ 'readonly-bordered-input': detailsReadonly },
						]"
						:readonly="detailsReadonly"
					/>
				</div>

				<div class="flex gap-2">
					<AppInput
						v-model="loan.kadasterRanking"
						label="Rangorde"
						:value-type="ValueType.Number"
						:precision="0"
						:class="[
							'max-w-50',
							{ 'readonly-bordered-input': detailsReadonly },
						]"
						:readonly="detailsReadonly"
					/>
				</div>
			</template>
		</div>

		<div v-if="showDetails">
			<h3 class="px-3 pb-1 text-md">Leningdelen</h3>
			<ul class="flex flex-col border-t">
				<template v-if="loan.mortgageParts">
					<li
						v-for="(part, idx) in loan.mortgageParts"
						:key="part.id"
						:class="{
							'border-b': idx !== loan.mortgageParts.length - 1,
						}"
					>
						<ExistingMortgageSinglePart
							v-model="loan.mortgageParts[idx]"
							:collapsed="
								part.id !== lastAddedMortgagePartId &&
								loan.mortgageParts.length > 1
							"
							:readonly="detailsReadonly"
							@delete="loan.mortgageParts.splice(idx, 1)"
						/>
					</li>
				</template>
			</ul>

			<div v-if="!detailsReadonly" class="border-t">
				<AppButton
					variant="ghost"
					icon="add"
					class="text-gray-600 ml-1 my-1"
					@click="addMortgagePart"
				>
					Leningdeel toevoegen
				</AppButton>
			</div>
		</div>
	</AppCard>
</template>

<script setup lang="ts">
import { useProviders } from "@/composables/useProvider";
import { useProviderLenders } from "@/composables/useProviderLenders";
import type {
	ExistingMortgage,
	ExistingMortgagePart,
} from "@loanlink-nl/model-app";
import { computed, ref, watch } from "vue";
import { ValueType } from "@/enums/ValueType";
import { titleForMortgage } from "./title-for-mortgage";
import { useDossierDataStore } from "@/store/useDossierDataStore";
import { useDossierStore } from "@/store/useDossierStore";
import { getCustomerLabel } from "@/helpers/inventarisation/customers";
import { storeToRefs } from "pinia";
import { useZero, useQuery } from "@/composables/useZero";
import { mutators, queries } from "@loanlink-nl/zero";
import { DateTime } from "luxon";
import { useFeatureFlag } from "@/composables/useFeatureFlag";
import { mapInformatieBerichtToExistingMortgage } from "@/helpers/hdnInformatieMapper";
import {
	canEditMortgageReferenceFields,
	getInformatieAanvraagStatus,
	informatieAanvraagMatchesMortgage,
} from "./informatie-aanvraag-status";
import { toast } from "vue-sonner";

const hdnMortgageRequestEnabled = useFeatureFlag(
	"hdn-mortgage-request-enabled",
);

const loan = defineModel<ExistingMortgage>();
const props = defineProps<{
	collapsed?: boolean;
}>();
const emit = defineEmits<{
	(e: "delete"): void;
}>();

const dossierDataStore = useDossierDataStore();
const dossierStore = useDossierStore();
const z = useZero();
const { data } = storeToRefs(dossierDataStore);
const hasTwoCustomers = computed(() => data.value.customers.length >= 2);

const { data: informatieAanvragen, status: informatieAanvragenStatus } =
	useQuery(() =>
		queries.informatieAanvragen.forDossier({
			dossierId: dossierStore.selectedDossier?.id ?? "",
		}),
	);

const informatieAanvraag = computed(() => {
	if (!loan.value?.id || !informatieAanvragen.value) {
		return undefined;
	}

	return informatieAanvragen.value.find((ia) =>
		informatieAanvraagMatchesMortgage(ia, loan.value),
	);
});

const informatieAanvraagStatus = computed(
	() =>
		informatieAanvraag.value?.hdnDossier?.status ??
		(informatieAanvraag.value ? "sent" : undefined),
);
const informatieAanvraagStatusMessage = computed(
	() => informatieAanvraag.value?.hdnDossier?.statusMessage ?? undefined,
);

const statusDetails = computed(() =>
	getInformatieAanvraagStatus(
		informatieAanvraagStatus.value,
		informatieAanvraagStatusMessage.value,
	),
);

const statusIcon = computed(() => {
	return statusDetails.value.icon;
});

const statusIconColorClass = computed(() => {
	return statusDetails.value.colorClass;
});

const statusDisplayName = computed(() => {
	return `${statusDetails.value.displayName}${statusDetails.value.statusCodeSuffix ?? ""}`;
});

const statusDescription = computed(() => {
	return statusDetails.value.description;
});

const mortgageReferenceFieldsReadonly = computed(
	() =>
		!canEditMortgageReferenceFields(
			Boolean(informatieAanvraag.value),
			informatieAanvraagStatus.value,
		),
);

const hasSelectedLender = computed(() => Boolean(loan.value?.lenderCode));

const ownerOptions = computed(() => {
	const options = [{ label: "Samen", value: "both" }];
	for (const customer of data.value.customers) {
		options.push({
			label: getCustomerLabel(data.value.customers, customer.id, true),
			value: customer.id,
		});
	}
	return options;
});

const currentObjectOptions = computed(() => {
	const options: Array<{ label: string; value: string | undefined }> = [
		{ label: "Selecteren...", value: undefined },
	];

	for (const [index, currentObject] of (
		data.value.currentObjects?.objects ?? []
	).entries()) {
		const parts = [
			currentObject.address?.openbareRuimteNaam,
			currentObject.address?.huisnummer,
			currentObject.address?.woonplaatsNaam,
		].filter(Boolean);

		options.push({
			label: parts.join(" ") || `Object ${index + 1}`,
			value: currentObject.id,
		});
	}

	return options;
});

function hasMortgagePartDetails(part: ExistingMortgagePart) {
	const { id: _id, interestDeductionLaw, ...details } = part;

	return (
		Object.values(details).some((value) => value !== undefined) ||
		(interestDeductionLaw !== undefined && interestDeductionLaw !== "new")
	);
}

const hasMortgageDetails = computed(() => {
	if (!loan.value) {
		return false;
	}

	return (
		loan.value.amountRegistration !== undefined ||
		loan.value.nhg !== undefined ||
		(loan.value.kadasterRanking !== undefined &&
			loan.value.kadasterRanking !== 1) ||
		(loan.value.mortgageParts?.some(hasMortgagePartDetails) ?? false)
	);
});

const { lenderOptions, getLenderOption } = useProviderLenders();
const { providerCodeSupportsInformatiebericht } = useProviders();

const selectedLenderSupportsInformatiebericht = computed(() =>
	providerCodeSupportsInformatiebericht(loan.value?.lenderCode),
);

const showOwnerField = computed(
	() => hasSelectedLender.value && hasTwoCustomers.value,
);

const showCurrentObjectField = computed(
	() => (data.value.currentObjects?.objects.length ?? 0) > 0,
);

const showInformatieBerichtUi = computed(
	() =>
		hasSelectedLender.value &&
		selectedLenderSupportsInformatiebericht.value &&
		hdnMortgageRequestEnabled.value,
);

const showManualEditingButton = computed(
	() =>
		hasSelectedLender.value && selectedLenderSupportsInformatiebericht.value,
);

const showDetails = computed(
	() =>
		hasSelectedLender.value &&
		Boolean(
			informatieAanvraag.value?.response ||
			loan.value?.isManual ||
			hasMortgageDetails.value,
		),
);
const detailsReadonly = computed(() => !loan.value?.isManual);

const manualEditingButtonLabel = computed(() => {
	if (loan.value?.isManual) {
		return "Bewerken uitschakelen";
	}

	if (informatieAanvraag.value?.response) {
		return "Handmatig aanpassen";
	}

	return "Hypotheek handmatig invullen";
});

const requestMortgageDataValidation = computed(() => {
	if (informatieAanvraag.value) {
		return ["Actuele stand wordt al opgevraagd"];
	}

	if (!loan.value?.lenderCode) {
		return ["Selecteer een geldverstrekker"];
	}

	if (!loan.value?.lenderMortgageId) {
		return ["Voer een hypotheeknummer in"];
	}

	if (data.value.customers.length === 0) {
		return ["Voeg een klant met achternaam en geboortedatum toe"];
	}

	const owner = loan.value.owner;
	if (data.value.customers.length > 1 && owner === undefined) {
		return ["Selecteer de eigenaar van deze hypotheek"];
	}

	const customers =
		owner === "both" || owner === undefined
			? data.value.customers
			: data.value.customers.filter((c) => c.id === owner);

	const customerDataErrors = customers.flatMap((c) => {
		const errors: string[] = [];
		const customerLabel = getCustomerLabel(data.value.customers, c.id, true);

		if (!c.personDetails?.lastName) {
			errors.push(`${customerLabel}: Vul de achternaam in`);
		}

		if (!c.personDetails?.birthDate) {
			errors.push(`${customerLabel}: Vul de geboortedatum in`);
		}

		return errors;
	});

	if (customerDataErrors.length > 0) {
		return customerDataErrors;
	}

	return true;
});

function requestMortgageData() {
	if (requestMortgageDataValidation.value !== true) {
		return;
	}

	const currentLoan = loan.value;
	if (!currentLoan) {
		return;
	}

	const lenderCode = currentLoan.lenderCode;
	if (lenderCode === undefined) {
		return;
	}

	const mortgageId = currentLoan.lenderMortgageId;
	if (mortgageId === undefined) {
		return;
	}

	const selectedDossier = dossierStore.selectedDossier;
	if (selectedDossier === undefined) {
		return;
	}

	const owner = currentLoan.owner;
	const customers =
		owner === "both" || owner === undefined
			? data.value.customers
			: data.value.customers.filter((c) => c.id === owner);

	const customerData = customers.map((c) => ({
		lastname: c.personDetails.lastName!,
		birthDate: c.personDetails.birthDate!,
	}));

	if (customerData.length === 0) return;

	z.value.mutate(
		mutators.hdnInformatieAanvraagTable.create({
			id: crypto.randomUUID(),
			hdnDossierId: crypto.randomUUID(),
			dossierId: selectedDossier.id,
			agencyId: selectedDossier.agencyId,
			existingMortgageId: currentLoan.id,
			createdAt: DateTime.now().toMillis(),
			requestTraceNr: crypto.randomUUID(),
			providerCode: lenderCode,
			content: {
				mortgageNumber: mortgageId,
				lenderCode: lenderCode,
				customers: customerData,
			},
		}),
	);
}

function applyMortgageData() {
	if (!loan.value || !informatieAanvraag.value?.response) {
		return;
	}

	const mapped = mapInformatieBerichtToExistingMortgage({
		existingMortgage: loan.value,
		informatieBericht: informatieAanvraag.value.response,
		mortgageNumber: (
			informatieAanvraag.value?.content as { mortgageNumber?: string }
		)?.mortgageNumber,
	});

	if (mapped.status !== "mapped") {
		const descriptionByReason = {
			"missing-informatie":
				"De ontvangen reactie bevat geen bruikbare hypotheekgegevens.",
			"hypotheek-not-found":
				"Het ontvangen bericht bevat geen hypotheek met dit hypotheeknummer.",
			"hypotheek-ambiguous":
				"Het ontvangen bericht bevat meerdere hypotheken, waardoor geen unieke koppeling gemaakt kon worden.",
			"missing-loans":
				"De ontvangen hypotheek bevat geen leninggegevens om over te nemen.",
		} as const;

		toast.error("Gegevens meenemen is niet gelukt", {
			description: descriptionByReason[mapped.reason],
		});

		return;
	}

	Object.assign(loan.value, mapped.mortgage);
}

function toggleManualEditing() {
	if (!loan.value) {
		return;
	}

	loan.value.isManual = !loan.value.isManual;

	if (loan.value.isManual && (loan.value.mortgageParts?.length ?? 0) === 0) {
		addMortgagePart();
	}
}

watch(
	[
		() => loan.value?.lenderCode,
		informatieAanvraag,
		() => informatieAanvragenStatus.value === "complete",
	],
	(
		[lenderCode, currentInformatieAanvraag, currentInformatieAanvragenLoaded],
		previousValues,
	) => {
		if (!loan.value) {
			return;
		}

		if (!lenderCode) {
			loan.value.isManual = undefined;
			return;
		}

		if (!currentInformatieAanvragenLoaded || currentInformatieAanvraag) {
			return;
		}

		if (!providerCodeSupportsInformatiebericht(lenderCode)) {
			loan.value.isManual = true;

			if ((loan.value.mortgageParts?.length ?? 0) === 0) {
				addMortgagePart();
			}

			return;
		}

		if (
			previousValues?.[0] &&
			!providerCodeSupportsInformatiebericht(previousValues[0])
		) {
			loan.value.isManual = undefined;
		}
	},
	{ immediate: true },
);

watch(
	loan,
	() => {
		if (!loan.value) {
			return;
		}

		let amountCurrent = 0;
		let amountOriginal = 0;
		for (const part of loan.value?.mortgageParts ?? []) {
			amountCurrent += part.amountCurrent ?? 0;
			amountOriginal += part.amountOriginal ?? 0;
		}

		loan.value.amountCurrent = amountCurrent;
		loan.value.amountOriginal = amountOriginal;
	},
	{ immediate: true, deep: true },
);
const isCollapsed = ref(props.collapsed ?? true);
const lastAddedMortgagePartId = ref();

function addMortgagePart() {
	if (!loan.value) {
		return;
	}

	lastAddedMortgagePartId.value = crypto.randomUUID();

	loan.value.mortgageParts ??= [];
	loan.value.mortgageParts.push({
		id: lastAddedMortgagePartId.value,
	});
}

function removeMortgage() {
	emit("delete");
}
</script>
